import 'allsettled-polyfill';
import { useState, useRef, useMemo, useEffect } from 'react';
import defaultValidations from './validations';
import defaultInputTypes from './input-types'
import { ConfigContext } from './context/config-context';

export const FormsProvider = ({ validations: importedValidations = {}, inputTypes: inputTypesProp, children }) => {
	const asyncValidations = useRef({ ...defaultValidations, ...importedValidations });

	const unavailableValidations = useRef([]);

	const availableValidationsRef = useRef({})
	const [availableValidations, setAvailableValidationsState] = useState(availableValidationsRef.current);
	
	const requestedValidationsRef = useRef([]);
	const [rawRequestedValidations, setRawRequestedValidations] = useState(requestedValidationsRef.current);

	const requestedValidations = useMemo(() => {
		return rawRequestedValidations.filter(v => !availableValidations[v]);
	}, [rawRequestedValidations, availableValidations])

	const inputTypes = useMemo(() => {
		return {
			...defaultInputTypes,
			...(inputTypesProp || {}),
		}
	}, [inputTypesProp])

	const setAvailableValidations = (newValidations) => {
		availableValidationsRef.current = newValidations;
		setAvailableValidationsState(newValidations);
	}

	useEffect(() => {
		if(requestedValidations.length > 0) {
			const validationPromises = requestedValidations.map(key => asyncValidations.current[key]());

			Promise.allSettled(validationPromises).then(fetchedValidations => {
				const { successfulValidations, failedValidations } = fetchedValidations.reduce(({ successfulValidations, failedValidations }, validationResponse, nth) => {
					const validationKey = requestedValidations[nth];
					return {
						successfulValidations: {
							...successfulValidations,
							...(validationResponse.status === 'fulfilled' ? {[validationKey]: validationResponse.value.default} : {}),
						},
						failedValidations: [
							...failedValidations,
							...(validationResponse.status !== 'fulfilled' ? [validationKey] : []),
						],
					}
				}, { successfulValidations: {}, failedValidations: [] });

				setAvailableValidations({...availableValidationsRef.current, ...successfulValidations});
				unavailableValidations.current = [ ...unavailableValidations.current, ...failedValidations ];
			});
		}
	}, [requestedValidations])

	const requestValidations = (validationKeys = []) => {
		const missingValidations = validationKeys.filter(key => (
			!requestedValidationsRef.current.includes(key) && !unavailableValidations.current.includes(key) && asyncValidations.current[key]
		));

		if(missingValidations.length > 0) {
			requestedValidationsRef.current = [
				...requestedValidationsRef.current,
				...missingValidations,
			];
			setRawRequestedValidations(requestedValidationsRef.current);
		}
	}

	return <ConfigContext.Provider value={{ availableValidations, requestValidations, inputTypes }}>
		{children}
	</ConfigContext.Provider>
}